import type { ContentPart, ToolCall } from '../../../types'
import type { GroupContentPart, ResolvedPart } from './hooks/use-interleaved-parts'
import type { AssistantBodyProps } from './nested/assistant-body'
import type { MessageProps } from './index'

// ---------------------------------------------------------------------------
// Message-tree equality — the one place equality for the message tree lives.
//
// Everything downstream of `AssistantBody` is driven by reference
// identity: a part whose content is unchanged keeps its object, so memoized
// rows below can bail out without any secondary fingerprint. These predicates
// define what "unchanged" means, so there is a single answer to that question.
// ---------------------------------------------------------------------------

/**
 * Element-reference compare over two tool-call lists. Exact because the
 * store's patch layer (`sink-patches.ts`) is the single transcript writer and
 * it replaces exactly the tool call an update touched, keeping every other
 * element's identity — so "same reference" IS "unchanged", evolving
 * `toolUpdate` input included (each event lands as a fresh element). A
 * server refetch replaces elements wholesale, which costs one extra render
 * pass, never a missed one.
 */
export function toolCallsEqual(a?: Array<ToolCall>, b?: Array<ToolCall>): boolean {
  if (a === b) {
    return true
  }
  if (!a || !b || a.length !== b.length) {
    return false
  }

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false
    }
  }

  return true
}

// Serves both real part shapes flowing through the tree — a message's
// `ContentPart` list and a group's `GroupContentPart` list.
type ComparableContentPart = ContentPart | GroupContentPart

export function contentPartsEqual(a?: Array<ComparableContentPart>, b?: Array<ComparableContentPart>): boolean {
  if (a === b) {
    return true
  }
  if (!a || !b || a.length !== b.length) {
    return false
  }

  for (let i = 0; i < a.length; i++) {
    const partA = a[i]
    const partB = b[i]
    if (partA.type !== partB.type) {
      return false
    }
    if (partA.type === 'tool_call' && partB.type === 'tool_call') {
      if (partA.toolCallId !== partB.toolCallId) {
        return false
      }
      continue
    }
    // Thinking text is deliberately skipped: a reasoning delta must not
    // re-render a whole turn's subtree. `ThinkingPart` subscribes to the
    // thinking store instead (`store/thinking-stream.ts`).
    if (partA.type === 'text' && partB.type === 'text' && partA.text !== partB.text) {
      return false
    }
  }

  return true
}

/**
 * Whether a freshly derived part carries the same content as the one already
 * rendered at that key. `useInterleavedParts` keeps the previous object when
 * this holds, which is what lets the rows below memoize on reference alone —
 * a streaming tool call reaches its card because its `toolCall` reference
 * moved, not because a fingerprint string was recomputed to say so.
 */
export function resolvedPartsEqual(a: ResolvedPart, b: ResolvedPart): boolean {
  if (a.type !== b.type || a.stableKey !== b.stableKey) {
    return false
  }
  // Checking both discriminants narrows both sides — same runtime test as the
  // `a.type !== b.type` guard above, expressed so no cast is needed.
  if (a.type === 'tool_call' && b.type === 'tool_call') {
    return a.toolCall === b.toolCall
  }
  if (a.type === 'thinking' && b.type === 'thinking') {
    return a.text === b.text && a.messageId === b.messageId
  }
  return a.type === 'text' && b.type === 'text' && a.text === b.text
}

/**
 * Compile-time exhaustiveness for the gate comparators below: `Handled` must
 * cover every key of the prop type, so adding a prop without deciding how the
 * comparator treats it fails to compile instead of silently skipping renders.
 */
type AssertComparatorHandles<Props, Handled extends keyof Props> =
  Exclude<keyof Props, Handled> extends never ? true : { missing: Exclude<keyof Props, Handled> }

/**
 * Gate for the group container. Groups are identity-stable across streaming
 * deltas: `groupCompletedMessages` hands back the previous pass's object for
 * any turn whose messages are reference-unchanged, so a reference compare is
 * the full structural compare. The streaming (isMostRecent) or last-user turn
 * still renders per token.
 */
const _messagePropsHandled: AssertComparatorHandles<
  MessageProps,
  | 'group'
  | 'isMostRecent'
  | 'isLastUserGroup'
  | 'messageIndex'
  | 'onStagedActionClick'
  | 'onStagedActionCreated'
  | 'onReminderClick'
> = true
void _messagePropsHandled

export function areMessagePropsEqual(prev: MessageProps, next: MessageProps): boolean {
  if (prev.isMostRecent || next.isMostRecent) return false
  if (prev.isLastUserGroup || next.isLastUserGroup) return false

  return (
    prev.group === next.group &&
    prev.messageIndex === next.messageIndex &&
    prev.onStagedActionClick === next.onStagedActionClick &&
    prev.onStagedActionCreated === next.onStagedActionCreated &&
    prev.onReminderClick === next.onReminderClick
  )
}

/**
 * Gate for `AssistantBody`. During streaming the parent recreates
 * these arrays on every store tick even when nothing meaningful changed.
 *
 * `editing` is compared by its rendered fields only (`isEditMode`,
 * `formattedContent`): the object is rebuilt per parent render, its
 * `editedTextRef` is a stable ref by contract (mutated, never reassigned),
 * and its callbacks close only over `message.id` / `message.conversation_id`,
 * which the `message` check below already covers. `renderedContentRef` is a
 * stable ref by the same contract.
 */
const _assistantBodyPropsHandled: AssertComparatorHandles<
  AssistantBodyProps,
  | 'message'
  | 'isMostRecent'
  | 'isTurnActive'
  | 'editing'
  | 'thinkingContent'
  | 'groupToolCalls'
  | 'groupContentParts'
  // Deliberately skipped (see docblock):
  | 'renderedContentRef'
> = true
void _assistantBodyPropsHandled

export function areAssistantBodyPropsEqual(prev: AssistantBodyProps, next: AssistantBodyProps): boolean {
  if (
    prev.isMostRecent !== next.isMostRecent ||
    prev.isTurnActive !== next.isTurnActive ||
    prev.editing?.isEditMode !== next.editing?.isEditMode ||
    prev.editing?.formattedContent !== next.editing?.formattedContent
  ) {
    return false
  }

  // Only re-render for structural thinking changes (started/stopped), not
  // every token — the thinking row streams its own text.
  if (!!prev.thinkingContent?.trim() !== !!next.thinkingContent?.trim()) {
    return false
  }

  if (prev.message !== next.message) {
    if (prev.message.id !== next.message.id || prev.message.content !== next.message.content) {
      return false
    }
    if (!toolCallsEqual(prev.message.toolCalls, next.message.toolCalls)) {
      return false
    }
    if (!contentPartsEqual(prev.message.contentParts, next.message.contentParts)) {
      return false
    }
  }

  if (prev.groupToolCalls !== next.groupToolCalls && !toolCallsEqual(prev.groupToolCalls, next.groupToolCalls)) {
    return false
  }

  if (
    prev.groupContentParts !== next.groupContentParts &&
    !contentPartsEqual(prev.groupContentParts, next.groupContentParts)
  ) {
    return false
  }

  return true
}
